// src/models/Insights/insightsDateRange.ts
//
// Presets: Yesterday / 7 days / 30 days / Custom. Every fixed preset still derives from
// "yesterday in UTC" so windows always end at the last complete UTC day - including today
// would put one partial day in the reporting-coverage denominator, which is the exact defect
// that got "Today" and "Previous 24 hours" dropped as presets (Rule 1). Custom ranges are
// user input, not clock-derived, so they go through `validateCustomRange` instead of
// `getInsightsDateRange` - that validator is also what enforces Rule 1 (`to` may never be
// later than yesterday) and Rule 2 (both bounds are always required) for the picker path.
// Unlike the fixed presets, a custom range may legitimately have `from === to` (Rule 3 - see
// `DateRangeControls.jsx`'s `endMinDate = startDate.add(1, 'day')`, which this deliberately
// does NOT copy).
//
// Part B: the Custom picker is additionally bounded below by the org's actual earliest
// `DailyOrgRollup.event_date` (discovered via `InsightsDAO.getEarliestDataDate`, not computed
// here - this module has no Cube dependency). `validateCustomRange` takes that discovered
// value as `earliestAvailable` and rejects a `from` earlier than it, but only when it is
// actually known (non-null) - an unresolved or failed lookup must never fabricate a
// rejection based on a bound we don't actually have. `getFallbackMinSelectableDate` is the
// separate, purely presentational floor (365 days before yesterday) the picker widget itself
// falls back to for its `minDate` when the real bound is unknown - not a validation rule.

import type { Dayjs } from 'dayjs';

export type InsightsRangePreset = 'yesterday' | '7days' | '30days' | 'custom';

/** The subset of presets `getInsightsDateRange` can resolve from the clock alone. */
type ClockResolvablePreset = Exclude<InsightsRangePreset, 'custom'>;

/** A closed, inclusive UTC date window as YYYY-MM-DD strings. Both bounds are always
 * present by construction - this is what `reporting_coverage_ratio` requires (Rule 2). */
export type InsightsDateRange = { from: string; to: string };

const FIXED_DAY_COUNT_PRESETS: Record<ClockResolvablePreset, number> = {
  yesterday: 1,
  '7days': 7,
  '30days': 30,
};

/** Days before yesterday the Custom picker's `minDate` falls back to when the real earliest
 * data date is unknown (Part B) - a lookup failure or a null result, treated identically. */
const FALLBACK_EARLIEST_SELECTABLE_LOOKBACK_DAYS = 365;

function toUtcDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Midnight UTC of the day before `now`. */
function utcYesterday(now: Date): Date {
  const startOfToday = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
  startOfToday.setUTCDate(startOfToday.getUTCDate() - 1);
  return startOfToday;
}

/**
 * Resolves a clock-derived preset to a closed UTC date range. `now` is injectable so tests
 * can pin the clock (e.g. 00:05 UTC boundary checks) without mocking global time.
 *
 * `'custom'` is not resolvable from the clock - it always throws. Custom ranges must come
 * from `validateCustomRange`, which validates user-picked bounds instead of deriving them.
 */
export function getInsightsDateRange(
  preset: InsightsRangePreset,
  now: Date = new Date(),
): InsightsDateRange {
  if (preset === 'custom') {
    throw new Error(
      "getInsightsDateRange() cannot resolve the 'custom' preset - custom ranges come from " +
        'validated user input (see validateCustomRange), never from the clock.',
    );
  }

  const to = utcYesterday(now);
  const dayCount = FIXED_DAY_COUNT_PRESETS[preset];

  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - (dayCount - 1));

  return { from: toUtcDateString(from), to: toUtcDateString(to) };
}

/**
 * Yesterday in UTC as a `YYYY-MM-DD` string - the latest date either custom-range picker may
 * select (Rule 1). Pass this straight into `dayjs(...)` for the pickers' `maxDate`: a plain
 * `YYYY-MM-DD` string parses as local midnight of that calendar date, so it is immune to the
 * same UTC-instant/local-timezone mismatch as Trap 2 below. Never round-trip it through a JS
 * `Date` object first - `dayjs(new Date(...))` reinterprets the instant in the browser's local
 * offset and can land on a different calendar day for users west of UTC.
 */
export function getMaxSelectableDate(now: Date = new Date()): string {
  return toUtcDateString(utcYesterday(now));
}

/**
 * The Custom picker's `minDate` floor when the org's real earliest data date is unknown
 * (Part B) - `InsightsDAO.getEarliestDataDate` returned `null` or threw. This is a
 * presentational default only, not a data-correctness bound: a `from` earlier than the
 * org's true earliest date still produces a valid (if sparse/zero) query, per the gold
 * README's "closed range with zero matching rows renders as a real 0%" behavior, so this
 * floor exists purely to keep the picker widget from scrolling back indefinitely.
 */
export function getFallbackMinSelectableDate(now: Date = new Date()): string {
  const floor = utcYesterday(now);
  floor.setUTCDate(floor.getUTCDate() - FALLBACK_EARLIEST_SELECTABLE_LOOKBACK_DAYS);
  return toUtcDateString(floor);
}

/**
 * Converts a date-picker's value to a `YYYY-MM-DD` string using dayjs's own local calendar
 * fields (Trap 2). A `DatePicker` value is local midnight of the day the user picked - routing
 * it through `Date.prototype.toISOString().slice(0, 10)` instead would reinterpret that local
 * midnight in UTC and can shift the date by a day for users east of UTC. `dayjs#format` reads
 * the value's own year/month/day, so it always echoes back the exact calendar date the user
 * selected regardless of the runtime's UTC offset.
 */
export function formatPickerDate(value: Dayjs): string {
  return value.format('YYYY-MM-DD');
}

export type CustomRangeValidationFailureReason =
  | 'missing_from'
  | 'missing_to'
  | 'from_after_to'
  | 'to_after_yesterday'
  | 'from_before_earliest_available';

export type CustomRangeValidationResult =
  | { ok: true; range: InsightsDateRange }
  | { ok: false; reason: CustomRangeValidationFailureReason };

/**
 * Validates a user-picked custom range against the same invariants the fixed presets get for
 * free from the clock: both bounds present (Rule 2), `from` on or before `to` (a single-day
 * range, `from === to`, is valid - Rule 3), and `to` no later than yesterday UTC (Rule 1).
 * `now` is injectable for the same reason as `getInsightsDateRange`.
 *
 * `earliestAvailable` (Part B) is the org's real earliest `DailyOrgRollup.event_date`, as
 * discovered by `InsightsDAO.getEarliestDataDate` - pass `null` when that lookup hasn't
 * resolved yet, failed, or returned no data. A `null` earliestAvailable skips this check
 * entirely rather than rejecting on a bound we don't actually have.
 */
export function validateCustomRange(
  from: string | null,
  to: string | null,
  now: Date = new Date(),
  earliestAvailable: string | null = null,
): CustomRangeValidationResult {
  if (!from) return { ok: false, reason: 'missing_from' };
  if (!to) return { ok: false, reason: 'missing_to' };
  if (from > to) return { ok: false, reason: 'from_after_to' };

  const maxSelectableDate = getMaxSelectableDate(now);
  if (to > maxSelectableDate) return { ok: false, reason: 'to_after_yesterday' };

  if (earliestAvailable && from < earliestAvailable) {
    return { ok: false, reason: 'from_before_earliest_available' };
  }

  return { ok: true, range: { from, to } };
}
